import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { DrugAutoCompleteItem, IndividualDrugItem } from 'src/app/shared/interfaces/drug-autocomplete-item.interface';
import { debounceTime, tap, switchMap } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { StateStructure } from '@ptl/state';
import { NgRedux, select } from '@ptl/angular-redux-store';
import { of, from, Observable } from 'rxjs';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  MedicationStateStructure,
  SelectedMedication,
  setIndividualMedications
} from '../reducers/medication.reducer';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PtlWindowLocationService } from '@ptl/global-components';
import { IndividualMedInfoService } from './services/individual-med-info.service';
import { plansNeedRefreshedFromBackend } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';

@Component({
    selector: 'app-individual-med-info',
    templateUrl: './individual-med-info.component.html',
    styleUrls: ['./individual-med-info.component.scss'],
    standalone: false
})
export class IndividualMedInfoComponent implements OnInit {

  @Input() nextButtonUrl: string;
  @Input() backButtonUrl: string;
  @Input() componentHeader = 'Find Medications';
  @Input() componentSubTitle = '(Optional)';
  @Input() componentDescription = 'Add any medications you\'re currently taking to the search. This will better refine your results.';
  @Input() autoCompleteSearchLabel = 'Search for Medications';
  @Input() autocompletePlaceholder = 'e.g. Atorvastatin';
  @Input() backButtonLabel = 'Back';
  @Input() nextButtonLabel = 'Next';

  @ViewChild('autoTrigger', { read: MatAutocompleteTrigger, static: true })
  autoTrigger: MatAutocompleteTrigger;

  @select(['componentStates', 'MedicationInformation'])
  medicationInformation$: Observable<MedicationStateStructure>;

  selectedMedications: IndividualDrugItem[] = [];
  drugSearchResults: IndividualDrugItem[] = [];
  drugsAvailalbleForSelection: IndividualDrugItem[] = [];
  autoCompleteLookupResults: IndividualDrugItem[];
  autocompleteForm: UntypedFormGroup;

  isLoadingAutocomplete: boolean = false;
  isLoadingDrugResults: boolean = false;
  hasSearchedDrugs: boolean = false;
  hasNoAutocompleteResults: boolean = false;
  hasEnoughSearchChars: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private individualMedService: IndividualMedInfoService
  ) {
    // Build form
    this.autocompleteForm = this.fb.group({
      userSearch: this.fb.control(null)
    });

    // Update med list display as it changes
    this.medicationInformation$.subscribe((state: MedicationStateStructure) => {
      if (!state || !state.individualMedications) {
        this.selectedMedications = [];
      }
      else {
        this.selectedMedications = state.individualMedications;
      }
    });
  }

  ngOnInit() {
    //Subscribe to changes on user's searched value
    this.autocompleteForm
      .get('userSearch')
      .valueChanges.pipe(
        // Hesitate to do more
        debounceTime(300),
        // Call the service
        switchMap((searchString: string) => {
          this.isLoadingAutocomplete = true;
          
          if (searchString.length > 2) {
            this.hasEnoughSearchChars = true;
            return from(this.individualMedService.autocompleteByName(searchString));
          }
          else {
            this.hasEnoughSearchChars = false;
            return of([]);
          }
        }),
        // Filter the results before assigning the values
        tap((results: IndividualDrugItem[]) => {
          this.isLoadingAutocomplete = false;
          this.autoCompleteLookupResults = [];

          results.forEach((tempDrug: IndividualDrugItem) => {
            if (!this.autoCompleteLookupResults.filter((existingDrug) => existingDrug.name === tempDrug.name).length){
              this.autoCompleteLookupResults.push(tempDrug);
            }
          });

          if (!this.autoCompleteLookupResults.length) {
            this.hasNoAutocompleteResults = true;
          }
          else {
            this.hasNoAutocompleteResults = false;
          }
        })
      )
      .subscribe();
  }

  filterDrugResults() {
    this.drugsAvailalbleForSelection = [];

    for (let i = 0; i < this.drugSearchResults.length; i++) {
      let curDrug = this.drugSearchResults[i];

      if (!this.selectedMedications.filter((selectedMed) => selectedMed.rxcui === curDrug.rxcui).length){
        this.drugsAvailalbleForSelection.push(curDrug);
      }
    }
  }

  /** Attempt to workaround the shadowdom issue of not appropriately handling the panel opening. */
  triggerPanelOpen() {
    this.autoTrigger.openPanel();
  }

  async addDrugToSelected(addedDrug: IndividualDrugItem, drugResultsIndex: number): Promise<void> {
    /** Add to selected */
    this.selectedMedications.push(addedDrug);
    this.ngRedux.dispatch(setIndividualMedications(this.selectedMedications));

    this.filterDrugResults();
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
  }

  async findAllDrugsFromName(drugAutocompleteSelection: IndividualDrugItem): Promise<void> {
    this.hasSearchedDrugs = true;
    this.isLoadingDrugResults = true;

    const drugLookupResponse: IndividualDrugItem[] = await this.individualMedService.searchByName(drugAutocompleteSelection.name);
    this.drugSearchResults = drugLookupResponse;

    this.isLoadingDrugResults = false;
    this.filterDrugResults();
  }

  removeDrugFromSelected(drugToRemove: IndividualDrugItem, index: number): void {
    this.selectedMedications.splice(index, 1);
    this.ngRedux.dispatch(setIndividualMedications(this.selectedMedications))

    this.filterDrugResults();
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
  }

  submit(form: UntypedFormGroup) {
    this.windowLocationService.goToUrl(this.nextButtonUrl);
  }

  navBack() {
    this.windowLocationService.goToUrl(this.backButtonUrl);
  }
}
